import { useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { useSettings } from '../context/SettingsContext'
import { useFinance } from '../context/FinanceContext'
import PageShell from '../components/layout/PageShell'
import GlassCard from '../components/ui/GlassCard'
import Button from '../components/ui/Button'

const FILING_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'single', label: 'Single' },
  { value: 'mfj', label: 'Married Filing Jointly' },
  { value: 'mfs', label: 'Married Filing Separately' },
  { value: 'hoh', label: 'Head of Household' },
]

export default function Settings() {
  const { user, logout } = useAuth()
  const { navigate } = useFinance()
  const {
    settings,
    updateProfile,
    setCalculatorDefaults,
    resetCalculatorDefaults,
    resetAll,
    DEFAULT_SETTINGS,
  } = useSettings()

  const [signingOut, setSigningOut] = useState(false)
  const [expandedCalc, setExpandedCalc] = useState(null)

  const handleSignOut = async () => {
    setSigningOut(true)
    try {
      await logout()
    } catch {
      setSigningOut(false)
    }
  }

  const toggleCalc = (key) => {
    setExpandedCalc(expandedCalc === key ? null : key)
  }

  const p = settings.profile
  const cd = settings.calculatorDefaults

  return (
    <PageShell
      title="Settings"
      subtitle="Manage your profile, preferences, and calculator defaults"
    >
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Account */}
        <GlassCard>
          <h3 className="text-sm font-semibold text-body mb-4">Account</h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-heading font-medium">{user?.displayName || user?.email}</p>
              {user?.displayName && (
                <p className="text-xs text-muted">{user.email}</p>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSignOut}
              disabled={signingOut}
              className="!text-negative hover:!bg-negative/10"
            >
              {signingOut ? 'Signing out...' : 'Sign Out'}
            </Button>
          </div>
        </GlassCard>

        {/* Profile */}
        <GlassCard>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-body">Profile</h3>
              <p className="text-xs text-muted mt-0.5">Your financial profile</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SettingsField
              label="Age"
              value={p.age}
              onChange={(v) => updateProfile({ age: v })}
              type="number"
              placeholder="e.g., 30"
              min={0}
              max={120}
            />
            <SettingsField
              label="Annual Income"
              value={p.income}
              onChange={(v) => updateProfile({ income: v })}
              placeholder="e.g., $75,000"
            />
            <div>
              <label className="block text-xs font-medium text-muted mb-1.5">Filing Status</label>
              <select
                value={p.filingStatus}
                onChange={(e) => updateProfile({ filingStatus: e.target.value })}
                className="w-full bg-input border border-input rounded-xl py-2.5 px-3 text-sm text-heading focus:outline-none focus:ring-1 appearance-none"
              >
                {FILING_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <SettingsField
              label="Dependents"
              value={p.dependents}
              onChange={(v) => updateProfile({ dependents: v })}
              placeholder="e.g., 2"
              type="number"
              min={0}
            />
            <SettingsField
              label="State"
              value={p.state}
              onChange={(v) => updateProfile({ state: v })}
              placeholder="e.g., Ohio"
            />
          </div>

        </GlassCard>

        {/* Calculator Defaults */}
        <GlassCard className="!p-0 overflow-hidden">
          <div className="px-6 py-4">
            <h3 className="text-sm font-semibold text-body">Calculator Defaults</h3>
            <p className="text-xs text-muted mt-0.5">Starting values for scenario calculators</p>
          </div>

          {/* Investment */}
          <CalcSection
            title="Investment Calculator"
            expanded={expandedCalc === 'investment'}
            onToggle={() => toggleCalc('investment')}
            onReset={() => resetCalculatorDefaults('investment')}
          >
            <div className="grid grid-cols-2 gap-4">
              <SettingsField
                label="Initial Amount"
                value={cd.investment.initialAmount}
                onChange={(v) => setCalculatorDefaults('investment', { initialAmount: parseNum(v) })}
                type="number"
                prefix="$"
                min={0}
              />
              <SettingsField
                label="Monthly Contribution"
                value={cd.investment.monthlyContribution}
                onChange={(v) => setCalculatorDefaults('investment', { monthlyContribution: parseNum(v) })}
                type="number"
                prefix="$"
                min={0}
              />
              <SettingsField
                label="Annual Return"
                value={cd.investment.annualReturn}
                onChange={(v) => setCalculatorDefaults('investment', { annualReturn: parseNum(v) })}
                type="number"
                suffix="%"
                step={0.1}
                min={0}
              />
              <SettingsField
                label="Time Horizon"
                value={cd.investment.years}
                onChange={(v) => setCalculatorDefaults('investment', { years: parseNum(v) })}
                type="number"
                suffix="years"
                min={1}
                max={50}
              />
            </div>
          </CalcSection>

          {/* Retirement */}
          <CalcSection
            title="Retirement Calculator"
            expanded={expandedCalc === 'retirement'}
            onToggle={() => toggleCalc('retirement')}
            onReset={() => resetCalculatorDefaults('retirement')}
          >
            <div className="grid grid-cols-2 gap-4">
              <SettingsField
                label="Current Age"
                value={cd.retirement.currentAge}
                onChange={(v) => setCalculatorDefaults('retirement', { currentAge: parseNum(v) })}
                type="number"
                min={20}
                max={70}
              />
              <SettingsField
                label="Retirement Age"
                value={cd.retirement.retirementAge}
                onChange={(v) => setCalculatorDefaults('retirement', { retirementAge: parseNum(v) })}
                type="number"
                min={50}
                max={75}
              />
              <SettingsField
                label="Current Salary"
                value={cd.retirement.currentSalary}
                onChange={(v) => setCalculatorDefaults('retirement', { currentSalary: parseNum(v) })}
                type="number"
                prefix="$"
                min={0}
              />
              <SettingsField
                label="Salary Growth"
                value={cd.retirement.salaryGrowth}
                onChange={(v) => setCalculatorDefaults('retirement', { salaryGrowth: parseNum(v) })}
                type="number"
                suffix="%/yr"
                step={0.1}
                min={0}
              />
              <SettingsField
                label="Years of Service"
                value={cd.retirement.yearsOfService}
                onChange={(v) => setCalculatorDefaults('retirement', { yearsOfService: parseNum(v) })}
                type="number"
                min={0}
                max={45}
              />
              <div>
                <label className="block text-xs font-medium text-muted mb-1.5">Plan Preset</label>
                <select
                  value={cd.retirement.planPreset}
                  onChange={(e) => setCalculatorDefaults('retirement', { planPreset: e.target.value })}
                  className="w-full bg-input border border-input rounded-xl py-2.5 px-3 text-sm text-heading focus:outline-none focus:ring-1 appearance-none"
                >
                  <option value="strs_ohio">STRS Ohio</option>
                  <option value="calstrs">CalSTRS</option>
                  <option value="generic">Custom Plan</option>
                </select>
              </div>
            </div>
          </CalcSection>

          {/* Loan */}
          <CalcSection
            title="Loan Calculator"
            expanded={expandedCalc === 'loan'}
            onToggle={() => toggleCalc('loan')}
            onReset={() => resetCalculatorDefaults('loan')}
          >
            <div className="grid grid-cols-2 gap-4">
              <SettingsField
                label="Home / Loan Price"
                value={cd.loan.principal}
                onChange={(v) => setCalculatorDefaults('loan', { principal: parseNum(v) })}
                type="number"
                prefix="$"
                min={0}
              />
              <SettingsField
                label="Down Payment"
                value={cd.loan.downPayment}
                onChange={(v) => setCalculatorDefaults('loan', { downPayment: parseNum(v) })}
                type="number"
                prefix="$"
                min={0}
              />
            </div>
          </CalcSection>

          {/* Roth vs Traditional */}
          <CalcSection
            title="Roth vs Traditional"
            expanded={expandedCalc === 'rothVsTraditional'}
            onToggle={() => toggleCalc('rothVsTraditional')}
            onReset={() => resetCalculatorDefaults('rothVsTraditional')}
            last
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-muted mb-1.5">Filing Status</label>
                <select
                  value={cd.rothVsTraditional.filingStatus}
                  onChange={(e) => setCalculatorDefaults('rothVsTraditional', { filingStatus: e.target.value })}
                  className="w-full bg-input border border-input rounded-xl py-2.5 px-3 text-sm text-heading focus:outline-none focus:ring-1 appearance-none"
                >
                  <option value="single">Single</option>
                  <option value="mfj">Married Filing Jointly</option>
                  <option value="hoh">Head of Household</option>
                </select>
              </div>
              <SettingsField
                label="Gross Income"
                value={cd.rothVsTraditional.grossIncome}
                onChange={(v) => setCalculatorDefaults('rothVsTraditional', { grossIncome: parseNum(v) })}
                type="number"
                prefix="$"
                min={0}
              />
              <SettingsField
                label="Current Age"
                value={cd.rothVsTraditional.currentAge}
                onChange={(v) => setCalculatorDefaults('rothVsTraditional', { currentAge: parseNum(v) })}
                type="number"
                min={18}
                max={70}
              />
              <SettingsField
                label="Retirement Age"
                value={cd.rothVsTraditional.retirementAge}
                onChange={(v) => setCalculatorDefaults('rothVsTraditional', { retirementAge: parseNum(v) })}
                type="number"
                min={50}
                max={80}
              />
              <SettingsField
                label="Annual Contribution"
                value={cd.rothVsTraditional.annualContribution}
                onChange={(v) => setCalculatorDefaults('rothVsTraditional', { annualContribution: parseNum(v) })}
                type="number"
                prefix="$"
                min={0}
              />
              <SettingsField
                label="Expected Return"
                value={cd.rothVsTraditional.expectedReturn}
                onChange={(v) => setCalculatorDefaults('rothVsTraditional', { expectedReturn: parseNum(v) })}
                type="number"
                suffix="%"
                step={0.1}
                min={0}
              />
              <SettingsField
                label="State"
                value={cd.rothVsTraditional.selectedState}
                onChange={(v) => setCalculatorDefaults('rothVsTraditional', { selectedState: v })}
                placeholder="e.g., OH"
              />
            </div>
          </CalcSection>
        </GlassCard>

        {/* Reset All */}
        <div className="text-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={resetAll}
            className="!text-negative hover:!bg-negative/10"
          >
            Reset all settings to defaults
          </Button>
        </div>
      </div>
    </PageShell>
  )
}

function parseNum(v) {
  const n = parseFloat(v)
  return isNaN(n) ? 0 : n
}

function SettingsField({ label, value, onChange, type = 'text', placeholder, prefix, suffix, step, min, max, multiline }) {
  const inputClasses = `w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:ring-1 ${
    prefix && suffix ? 'pl-7 pr-16' : prefix ? 'pl-7 pr-3' : suffix ? 'pl-3 pr-16' : 'px-3'
  }`

  if (multiline) {
    return (
      <div>
        <label className="block text-xs font-medium text-muted mb-1.5">{label}</label>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-input border border-input rounded-xl px-3 py-2.5 text-sm text-heading placeholder-faint focus:outline-none focus:ring-1 resize-none h-20"
        />
      </div>
    )
  }

  return (
    <div>
      <label className="block text-xs font-medium text-muted mb-1.5">{label}</label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-faint">{prefix}</span>
        )}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(type === 'number' ? e.target.value : e.target.value)}
          placeholder={placeholder}
          step={step}
          min={min}
          max={max}
          className={inputClasses}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-faint">{suffix}</span>
        )}
      </div>
    </div>
  )
}

function CalcSection({ title, expanded, onToggle, onReset, children, last }) {
  return (
    <div className={last ? '' : 'border-b border-subtle'}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-3 text-sm font-medium text-heading hover:bg-subtle transition"
      >
        <span>{title}</span>
        <svg className={`w-4 h-4 text-muted transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expanded && (
        <div className="px-6 pb-4">
          {children}
          <div className="mt-3 text-right">
            <button
              onClick={onReset}
              className="text-xs text-muted hover:text-heading transition underline"
            >
              Reset to defaults
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
